import React, {forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import {useLocation, useNavigate} from 'react-router-dom';
import Editor from "../component/Editor";
import {getLangTemplates, saveCode} from '../ApiService';
import {getDebugTrace} from '../PyTutorService';
import he from 'he';
import ShareResult from "../component/ShareResult";
import ToastService from "../component/ToastService";
import {wsInput, wsListen, wsRunCode} from "../WsService";
import * as postRobot from "post-robot";
import TU from '@oscloudlab/thinkup'
import {useTranslation} from "react-i18next";
import FlexLayout from "./FlexLayout";
import Icons from "./icons/Icons";
import {Dropdown, message, Modal, Space, Spin, Tooltip} from "antd";
import CustomEnvModal from "./CustomEnvModal";
import Timer from './Timer';
import {fitLangMap} from '../util/const';
import settingPng from '../component/icons/png/setting.png';
import listEmptyPng from '../component/icons/png/listEmpty.png';
import Debug from "./Debug";
import {checkBind, checkLogin, login} from "../util/login";
import VgenAdv from "./VgenAdv";

const CustomEnvRegex = /^.+自定义环境.(.*)/;
const debugAbleLangs=["python","python3","c","javascript","java","cpp"];
const Acm = forwardRef((props, ref) => {

  const {
    lang,
    code,
    updateCode,
    updateLang,
    preview,
    updatePreview,
    sourceWindow,
    aiEnabled,
    embedded,
    oid,
    oldCode,
    setOldCode,
    oldLang,
    langs,
    autoRun,
    readOnly,
    onRunResult,
    initDark,
    sendYuqueCode,
    customButtons,
    onLoad,
    platform,
    setCloseCallback,
    handleDebugStatus,
    setAiCollapsed,
    authInfo
  } = props;

  const [dark, setDark] = useState(!!initDark)

  const [htmlCode, updateHtmlCode] = useState("");
  const [langIndex, updateLangIndex] = useState(0);
  const [showShare, setShowShare] = useState(false);
  const [running, setRunning] = useState(false);
  const [runFailed, setRunFailed] = useState(false);
  const [runFinished, setRunFinished] = useState(false);
  const [command, setCommand] = useState("")
  const terminalRef = useRef(null);
  const layoutRef = useRef(null);

  const [notices, setNotices] = useState([]);
  const [newOutput, setNewOutput] = useState("")
  const [loading, setLoading] = useState(false)
  const [modified, setModified] = useState(false)
  const [envList, setEnvList] = useState([]);
  const [selectedEnv, setSelectedEnv] = useState(null);
  const [envStatus, setEnvStatus] = useState(0);
  const [envListShow, setEnvListShow] = useState(false);
  const [customEnvModalOpen, setCustomEnvModalOpen] = useState(false);
  const [packageList, setPackageList] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [authorEnv, setAuthorEnv] = useState(null);
  const [initPackages, setInitPackages] = useState([]);
  const [envListMenu, setEnvListMenu] = useState({});
  const [langConfig, setLangConfig] = useState(null);
  const [inIframe, setInIframe] = useState(false);
  const [envNameMaxWidth, setEnvNameMaxWidth] = useState(148);
  const [resizeObserverArr, setResizeObserverArr] = useState([]);
  const DebugStatus = {
    none: null,
    showButton: "调试",
    analysing: "分析中...",
    analysingFailed: "无法调试",
    debugging: "调试中",
    restart: "重新调试"
  }
  const [debugStatus, setDebugStatus] = useState(null);
  const [debugDat, setDebugDat] = useState(null);
  const [debugLine, setDebugLine] = useState(null);
  const [exception, setException] = useState(null);
  const [breakPoints, setBreakPoints] = useState([]);
  const [svgResult, setSvgResult] = useState(null);
  //刚刚创建的Env, 里面存id, 有值时，说明刚刚创建成功
  const [newEnvId, setNewEnvId] = useState(null);

  const codeRef = useRef(code);
  const langRef = useRef(lang);
  const previewRef = useRef(preview);
  const langConfigRef = useRef(langConfig);
  const labCode = useRef(null);


  const {pathname} = useLocation();
  const yuque = pathname.startsWith('/yuque');

  const queryParams = new URLSearchParams(location.search);
  const fromVgen = queryParams.get("fromVgen");

  const [showVgenAdv, setShowVgenAdv] = useState(false);
  const [vgenAdvDismissed, setVgenAdvDismissed] = useState(false);

  // 使子组件的方法对父组件可用
  useImperativeHandle(ref, () => ({
    runCode, updateLayout:layoutRef.current?.updateLayout,
  }));

  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    setInIframe(window.parent !== window)
    if (authInfo?.isLogin) {
      wsListen(wsHandler, authInfo.token);
    }

    let emRunListener = postRobot.on('run', runCode);
    let emSaveListener = postRobot.on('save', onEmSave);
    let vgenPackagesListener = postRobot.on('vgenPackages', (e) => {
      toCreate()
      setInitPackages(e.data.packages);
      setEnvListShow(true)
    });
    if (autoRun){
      runCode()
    }
    onLoad && onLoad();
    // 获取依赖包数据
    toGetPackList();

    return () => {
      emRunListener.cancel();
      emSaveListener.cancel();
      vgenPackagesListener.cancel();
    }
  }, []);

  // 监听头部宽度变化，并每次都清理ResizeObserver
  useEffect(() => {
    resizeObserverArr.forEach(observer => {
      observer?.disconnect();
    });
    const headerDom = document.querySelector('#codeHeaderId');
    const headerObserve = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const isCustomEnv = selectedEnv?.name !== envList[0]?.name;
        const headerWidth = entry.contentRect.width;
        const maxW = headerWidth - (isCustomEnv ? 266 : 156);
        setEnvNameMaxWidth(maxW);
      });
    });
    headerObserve.observe(headerDom);
    setResizeObserverArr([...resizeObserverArr, headerObserve]);

    return () => {
      resizeObserverArr.forEach(observer => {
        observer?.disconnect();
      });
    }
  }, [selectedEnv])

  useEffect(() => {
    let langConfig = fitLangMap.find(i => lang[0].toLowerCase() === i.lang);
    setLangConfig(langConfig)
    langConfigRef.current = langConfig;
    // 获取自定义环境列表
    if (lang?.[0] && langConfig) {
      getEnvList(langConfig);
    }
  }, [authInfo, lang]);

  const toSetCloseCallback = (cb) => {
    if (!setCloseCallback) return;
    if (!cb) {
      setCloseCallback(null);
      return;
    }
    setCloseCallback(() => () => {
      cb();
    });
  }

  const toGetPackList = () => {
    const url = fitLangMap.find(i => lang[0].toLowerCase().indexOf(i.lang) > -1)?.packagesUrl;
    if (!url) return;
    try {
      fetch(url)
        .then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          return response.json();
        })
        .then(data => {
          const { rows } = data;
          setPackageList(rows);
        })
        .catch(error => {
          console.log(error);
        });
    } catch (error) {
      console.log(error)
    }
  }

  const menuItemSelect = (e) => {
    const {key} = e;
    let item;
    if (langConfig?.versionList?.some(({ value }) => value === key)) {
      const selectedOption = langConfig.versionList.find(({ value }) => value === key);
      item = { id: key, name: selectedOption.label, type: selectedOption.type };
    } else if (key === '作者定义环境') {
      item = authorEnv;
    } else {
      item = envList.find(({id}) => {
        return id === key;
      });
    }
    if (! item){
      return;
    }
    setEnvListShow(false);
    setSelectedEnv(item);
    if (langConfig.lang === 'systemverilog') {
      setEnvStatus(2)
      return;
    }
    // setEnvListShow(false);
    let newCode = [...code]
    if (key !== envList[0]?.id) {
      setEnvStatus(0);
      const commentsFlag = langConfig.commentsFlag || '#'
      //添加代码自定义环境注释
      if (CustomEnvRegex.test(code[0])) {
        // 如果满足条件，则替换第一行内容
        newCode[0] = `${commentsFlag}自定义环境 ${item.image}\n${code[0].replace(CustomEnvRegex, '').trim()}`;
      } else {
        if (lang[0] === 'php') {
          code[0] = `${code[0].replace(/^<\?php.*/, '').trim()}`;
        }
        newCode[0] = `${commentsFlag}自定义环境 ${item.image}\n${code[0]}`;
      }
      updateCode(newCode);
    }else{
      //默认环境删除环境注释
      if (lang[0] === 'php') {
        newCode[0] = `${code[0].replace(CustomEnvRegex, '<?php').trim()}`;
      }else{
        newCode[0] = `${code[0].replace(CustomEnvRegex, '').trim()}`;
      }
      updateCode(newCode);
    }
  }

  const getEnvList = (langConfig, finishHandler) => {
    const defaultVersion = langConfig?.defaultEnvName;
    const defaultEnv = {
      id: defaultVersion,
      name: defaultVersion,
      default: true
    };
    setEnvList([defaultEnv]);
    authInfo.userInfo?.isBind && getLangTemplates(lang[0], authInfo.token).then(res => {
      const { data } = res;
      if (data) {
        setEnvList([defaultEnv, ...data]);
        finishHandler && finishHandler();
      }
    })
  }

  useEffect(() => {
    setSelectedEnv(envList[0]);
    //如果代码里包含 自定义环境 判断 envList 里是否有, 有则选中
    const matchResult = code[0]?.match(CustomEnvRegex);
    if (langConfig && matchResult && matchResult[1]) {
      // matchResult[1] 中包含了 #自定义环境 后面的信息
      const image = matchResult[1].trim(); // 去掉前后的空白
      let matched = false;
      image && envList.forEach(env => {
        if (env.image === image) {
          setSelectedEnv(env);
          matched = true;
        }
      });
      if (! matched) {
        let authorEnv = {
          id: '作者定义环境',
          name: '作者定义环境',
          image: image
        };
        setAuthorEnv(authorEnv);
        setSelectedEnv(authorEnv);
      }else {
        setAuthorEnv(null);
      }

    }
  }, [envList]);

  useEffect(() => {
    if (newEnvId){
      setNewEnvId( null)
      menuItemSelect({"key":newEnvId})
      message.success("已自动为您切换为刚刚创建的环境");
    }
  }, [envList, newEnvId]);


  const toCreate = async () => {
    if (checkBind(authInfo)){
      //fromVgen !== 'true' && setInitPackages([])
      setCustomEnvModalOpen(true);
    }
  }
  const openEnvManager = async () => {
    if (!authInfo.isLogin){
      login()
    }else if (authInfo.labInfo?.code){
      window.open(`${process.env.REACT_APP_CLOUD_LAB_URL}/admin/base/labenv?labCode=${authInfo.labInfo.code}&myEnv=true&title=我的环境`, '_blank');
    }
  }

  //重新渲染自定义环境列表
  useEffect(() => {
    if (!envList?.length) return null;
    const envArr = [...envList].map(({ id, name }) => ({ key: id, label: name }));
    envArr.shift();
    let listMenu = {
      style: { width: '340px', maxHeight: '400px' },
      selectable: true,
      onSelect: menuItemSelect,
      selectedKeys: [selectedEnv?.id],
      items: [],
    }
    // 如果语言是 systemverilog，添加特定选项
    if (lang[0] === 'systemverilog') {
      listMenu.items.push(
        {
          key: 'default0',
          label: 'Systemverilog 仿真器选项',
          disabled: true,
        },
        ...langConfigRef.current?.versionList.map(({ label, value }) => ({
          key: value,
          label: label,
        })),
        {
          type: 'divider',
        }
      );
    } else {
      // 添加默认和更多环境选项
      listMenu.items.push(
        {
          key: 'default0',
          label: '默认',
          disabled: true,
        },
        {
          key: envList[0]?.id,
          label: envList[0]?.name,
        },
        {
          type: 'divider',
        },
        {
          label: '更多环境',
          key: 'more',
          extra: (
            <span style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
              <div className="createCustomEnv" onClick={(event) => {
                openEnvManager()
                event.stopPropagation();
              }}>
                <img src={settingPng} color="#1677ff"/>
                管理
              </div>
              <div className="createCustomEnv"
                   style={{marginLeft: '10px'}}
                   onClick={(event) => {
                setInitPackages([])
                toCreate()
                event.stopPropagation();
              }}>
                <img src={settingPng} color="#1677ff"/>
                新建
              </div>
            </span>
          ),
          disabled: true,
        },
        ...envArr
      );
    }
    if (authorEnv){
    listMenu.items.splice(3, 0,
      {
        key: 'default',
        label: '作者定义环境',
        disabled: true,
      },
      {
        key: authorEnv?.id,
        label: authorEnv?.name,
      },{
        type: 'divider',
      }
      );
    }
    setEnvListMenu(listMenu);
  }, [envList, selectedEnv, customEnvModalOpen, packageList, lang, initPackages]);

  useEffect(() => {
    if (lang[0] === "html") {
      return null;
    }
    setCommand('')
    if (running) {
      //运行后terminal获取焦点
      terminalRef.current?.focus();//无效, timeout也无效,暂时忽略
    }else{
      //停止运行去掉闪烁光标
      const htmlContent = terminalRef.current?.innerHTML;
      terminalRef.current.innerHTML = htmlContent.replace(/<b.*?>/gi, '').replace(/<\/b>/gi, '');
    }
  }, [running])
  useEffect(() => {
    if(!newOutput){
      return;
    }
    updatePreview(preview + newOutput)
    scrollLogToBottom()
  }, [newOutput])

  useEffect(() => {
    previewRef.current = preview
    //运行中添加闪烁光标
    if (running && !loading) {
      const boldElement = document.createElement('b');
      boldElement.textContent = " "; // 设置 <b> 的文本
      boldElement.className = "blink";
      terminalRef.current?.appendChild(boldElement);
    }
  }, [preview])

  function langKey() {
    return (oid || "code") + "_" + lang[0];
  }

//更改语言
  useEffect(() => {
    setRunFinished(false)
    langRef.current = lang;
    clearDebug()
    setRunFailed(false)
    //读取缓存或者默认代码
    let codeJson = localStorage.getItem(langKey());
    if (codeJson){
      updateCode(JSON.parse(codeJson))
    }else if(oldLang === lang[0]){
      updateCode(oldCode)
    }else if(langs){
      updateCode([langs[lang[0]]?.helloworld])
    }
    //更新ai助手参数
    (aiEnabled || yuque) && TU.setInputs({lang:lang[0]});
    // 重新获取packageList
    toGetPackList();
  }, [lang]);
  //更改代码
  useEffect(() => {
    console.log("Code onChange useEffect")
    codeRef.current=code;
    if ([DebugStatus.debugging].includes(debugStatus)) {
      setDebugStatus(DebugStatus.restart)
    }
    if ([DebugStatus.analysingFailed].includes(debugStatus)) {
      setDebugStatus(DebugStatus.showButton)
    }
    //渲染html
    if (lang[0] === "html") {
      renderHtml(code);
    }
    //更新ai助手参数
    (aiEnabled || yuque) && TU.setInputs({code:code[0]})
    //嵌入式回调
    if (sourceWindow){
      postRobot.send(sourceWindow, 'change', code);
    }
    //判断是否修改
    refreshModified()
  }, [code]);
  useEffect(() => {
    if (dark) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [dark]);
  // 代码执行错误时，智能助理进行错误解析
  // useEffect(() => {
  //   runFailed && yuque && loadDify();
  // }, [runFailed]);

  const wsHandler = (runResult, finish, type) => {
    if (type === 'startResult') {
      runResult.status === 'SUCCESS' && setEnvStatus(2);
      runResult.status === 'STARTING' && setEnvStatus(1);
      if (runResult.status === 'FAIL') {
        setEnvStatus(0);
        message.warning('自定义环境启动失败，请重新运行。')
      }
      return;
    }
    setLoading(false)
    if (process.env.REACT_APP_DEBUG_WS === 'true') {
      console.log("运行日志", runResult);
    }

    if (finish){
      setRunning(false)
      setRunFinished(true)
      if (!runResult.success) {
        setRunFailed(true)
        setAiCollapsed(false)
        analyseError()
        onRunResult && onRunResult({failed:true,error:runResult.log});
        if (runResult.status === "TIMEOUT") {
          outputNotice(`=== ${t("等待或运行超时")} ===`);
        } else {
          outputNotice(`=== ${t("代码执行出错")} ===`);
        }
      }else {
        outputNotice(`=== ${t("代码执行成功")} ===`, true);
        // if (yuque){
        //   setShowVgenAdv(true);
        // }
      }
      setException(null)
      clearDebug()
      return;
    }
    if (runResult.status === 'SVG') {
      setSvgResult(runResult.log)
    }else{
      setNewOutput(runResult.log)
    }
    setNewOutput(undefined)
  };
  const startDebug =  () => {
    if (!checkLogin(authInfo, yuque)) return;
    checkBind(authInfo)
    if (noInput()) return;
    //修改按钮状态
    setDebugStatus(DebugStatus.analysing)
    //点调试按钮后调用
    getDebugTrace(langRef.current[0], codeRef.current[0]).then((data) => {
      console.log("python-tutor", data)
      if (successTrace(data.trace)) {
        setDebugDat(data)
        //处理错误标记
        for (let i = 0; i < data.trace.length; i++) {
          if (data.trace[i].exception_msg) {
            setException({exception_msg: data.trace[i].exception_msg, line: data.trace[i].line})
          }
        }
        setDebugStatus(DebugStatus.debugging)
        //上移错误诊断按钮, 避免挡住调试按钮
        const aiAssi = document.getElementById("aiAssistant");
        aiAssi && (aiAssi.style.bottom = "80px");
      }else{
        setDebugStatus(DebugStatus.analysingFailed)
      }
    }).catch(() => {
      setDebugStatus(DebugStatus.analysingFailed)
    })


  }
  const successTrace = (trace) => {
    if(! trace || trace.length === 0){
      return false;
    }
    if (
      ["uncaught_exception","exception"].includes(trace[0].event) ||
      ["uncaught_exception","exception"].includes(trace[1].event)
    ) {
      return false;
    }
    return true;
  };

  async function onEmSave(){
    let codePiece =  await saveCode(lang[0], code, null)
    return {oid: codePiece.oid}
  }

  const outputNotice = (msg, success) => {
    setNotices([{msg: msg, success}])
    scrollLogToBottom()
  }


  function clearLog() {
    setNotices([])
    setNewOutput(undefined)
    updatePreview("")
    setSvgResult(null)
    setShowVgenAdv(false)
  }
  function clearDebug(){
    if (debugAbleLangs.includes(langRef.current[0])) {
      setDebugStatus(DebugStatus.showButton)
      scrollLogToBottom()
    }else{
      setDebugStatus(DebugStatus.none)
    }
    setDebugLine(null)
    setException(null)
    setBreakPoints( [])
  }

  const runCode = () => {
    if (!checkLogin(authInfo, yuque)) return;
    checkBind(authInfo)
    // 运行按钮埋点
    try {
      postRobot.send(sourceWindow, 'runCodeClick');
    } catch (error) {}
    if (noInput()) return;
    if (canNotRun()) return;
    if (running) return;
    if (envStatus === 1) return;
    clearLog();
    clearDebug()
    setRunning(true);
    setLoading(true);
    setRunFailed(false);
    setRunFinished(false)
    onRunResult && onRunResult({failed:false});
    langRef.current=lang;
    codeRef.current=code;
    wsRunCode(lang[0], code[0], platform, envStatus, selectedEnv);
  };

  const noInput = () => {
    if (! code[0]){
      ToastService.show(t("请输入代码"), 3000)
      return true;
    }
    return false;
  };
  const canNotRun = () => {
    if (lang[0] === "html"){
      //ToastService.show("No need to run for HTML", 3000)
      return true;
    }
    return false;
  }

  const selectTab = (tabIndex) => {
    updateLangIndex(tabIndex)
  };
  const renderHtml = (code) => {
    setTimeout(() => {
      updateHtmlCode(`${code[0]||''}\n${code[1] ? `<style>${code[1]}</style>` : ''}\n<script>${code[2] ? code[2] : ''}</script>`);
    }, 50);

  };

  const loadDify = () => {
    const lang = langRef.current
    const code = codeRef.current
    const preview = previewRef.current
    TU.startConversation({lang:lang[0], code:code[0], error:preview},
      "错误解析", "app-0fMg5309Hkf1q4QMKrIFKrvO", "分析错误原因, 并给出解决方案",
      (response) => {
        console.log("dify callback");
    });

  }
  const analyseError = () => {
    const lang = langRef.current
    const code = codeRef.current
    const preview = previewRef.current
    TU.addResponseMessage("根据报错信息, 智能助手推荐您: ")
    TU.sendCard({title:"错误解析", content:"分析错误原因, 并提供解决方案"}, loadDify)
    //先判断错误类型
    console.log("analyze error type");
    //错误类型包括:
    // 缺少依赖 (在用)
    // 代码不完整 (在用)
    // 语法错误
    // 环境因素
    // 版本不匹配
    // 依赖冲突
    // 其他
    //https://dify15.oscollege.net/app/a4ae2e60-c164-4224-b931-259d49bb847a/configuration
    TU.callAgent({
      lang: lang[0],
      code: code[0],
      error: preview
    }, '只要纯json文本, 不要其他文字, 不要任何包装', 'app-WreoT4HG1Nm5jMrkV1OuJwy5')
      ?.then((res) => {
        //根据错误类型, 再调用不同的agent并显示不同的按钮
        console.log("error type response:", res);
        //条件 1, 可自定义语言 2. 语雀场景 或 非嵌入自建
        if (res.data?.reason === "缺少依赖") {
          if(langConfigRef.current?.packagesUrl) {
            //支持语言自动添加依赖
            return TU.callAgent({
              lang: lang[0],
              code: code[0],
              error: preview
            }, '只要纯json文本, 不要其他文字, 不要任何包装', 'app-HfcAKVYtFNSa0jmcnkb8BXFw')
              ?.then((res) => {
                console.log("callAgent callback", res);
                if (!res?.data?.length) {
                  return;
                }
                TU.sendCard({title: "创建自定义环境", content: "自动为您添加运行所需依赖"}, () => {
                  console.log(res.data)
                  setInitPackages(res.data);
                  setEnvListShow(true)
                  toCreate()
                })
              })
          }else{
            //不支持语言跳转自定义环境
            TU.sendCard({title: "配置自定义环境", content: "在云燕平台配置自定义环境, 具备指定自定义镜像, 运算资源 等 高级玩法. "}, () => {
              window.open(`${process.env.REACT_APP_CLOUD_LAB_URL}/admin/base/labenv/setEnv?labCode=${authInfo.labInfo.code}&userId=${authInfo.userInfo.userId}&title=我的环境&edited=true`, '_blank');
            })
          }
        } else if (res.data?.reason === "代码不完整") {
          TU.sendCard({title: "补全代码", content: "自动为您补全代码上下文, 使代码可执行"}, () => {
            console.log(res.data)
            //https://dify15.oscollege.net/app/33b8dc8e-f668-46c2-a4a2-df15dceb4daa/configuration
            return TU.callAgent({
              lang: lang[0],
              code: code[0],
              error: preview
            }, '只要纯json文本, 不要其他文字, 不要任何包装', 'app-tI4Rcp8LGATiylBuQkJ04jH1')
              ?.then((res) => {
                console.log("callAgent callback", res);
                if (res?.data?.reason !== "代码不完整" || !res?.data?.fullCode) {
                  return;
                }
                TU.sendCard({title: "应用代码", content: res.data.fullCode}, () => {
                  console.log("应用代码")
                  updateCode([res.data.fullCode])
                })
              })
          })
        }
      })
  }

  const handleKeyPress = (event) => {
    if (!running){
      return;
    }
    //有修饰键被按下不做处理(不包括Shift，Shift用于输入大写字母和特殊字符)
    if (event.ctrlKey || event.altKey || event.metaKey) {
      return;
    }
    if (event.key === 'Backspace') {
      if(!command){
          return;
      }
      setCommand(command?.slice(0, -1));
      updatePreview(preview.slice(0, -1));
    } else if (event.key === 'Enter') {
      updatePreview(preview + "\n")
      setCommand('')
      command && wsInput(command);
    }else if (event.key.length !== 1) {
      //ignore
    } else{
      updatePreview(preview + event.key)
      setCommand(command + event.key)

    }
  };
  //用户交互支持粘贴事件处理函数
  const handlePaste = (event) => {
    if (!running){
      return;
    }
    event.preventDefault(); // 阻止默认粘贴行为
    const pasteData = event.clipboardData.getData('text'); // 获取剪贴板内容
    setCommand(prev => prev + pasteData);
    updatePreview(prev => prev + pasteData);
  };

  const refreshModified = () => {
    if (oldLang !== lang[0] && langs) {
      const flag = code[0] !== langs[lang[0]]?.helloworld;
      setModified(flag);
      if (!setCloseCallback) return;
      if (flag) {
        toSetCloseCallback(updateYuque);
      } else {
        toSetCloseCallback(null);
      }
    } else if (oldCode){
      const flag = oldCode[langIndex] !== code[langIndex];
      setModified(flag);
      if (!setCloseCallback) return;
      if (flag) {
        toSetCloseCallback(updateYuque);
      } else {
        toSetCloseCallback(null);
      }
    }
  }

  const cancelEdit = () => {
    let confirm = window.confirm("确定要取消修改吗？");
    if (!confirm) {
      return;
    }
    localStorage.removeItem(langKey())
    if (oldLang !== lang[0]) {
      updateCode([langs[lang[0]]?.helloworld])
    } else if (oldCode !== code[0]) {
      updateCode(oldCode);
    }

  }

  const updateYuque = () => {
    setOldCode(code)
    setModified(false)
    sendYuqueCode(code[0])
  }

  const scrollLogToBottom = () => {
    const container = document.querySelector('.result');
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  useEffect(() => {
    // 点击事件处理函数
    const dropdownDom = document.getElementsByClassName('envlistDropdownDom')?.[0];
    // console.log(dropdownDom, 'dropdownDom--')
    if (!dropdownDom || !envListShow) return;
    const handleClickOutside = (event) => {
      // 判断点击的目标是否在 dropdown 区域外
      // console.log(dropdownDom.contains(event.target), 'dropdownDom.contains(event.target)')
      if (!dropdownDom.contains(event.target)) {
        setEnvListShow(false);
      }
    };
    // 添加全局点击事件监听
    document.addEventListener("click", handleClickOutside);
    // 清理函数：组件卸载时移除事件监听
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [envListShow]);
  useEffect(() => {
    handleDebugStatus && handleDebugStatus(debugStatus)
  }, [debugStatus]);

  const onStep = (step, out, finish, error) => {
    setDebugLine(step)
    updatePreview(out)
    if (finish) {
      if (error) {
        setTimeout(() => {
          outputNotice(`=== ${t("代码执行失败")}: ${error} ===`, false);
        }, 20);
      }else
      {
        setTimeout(() => {
          outputNotice(`=== ${t("代码执行成功")} ===`, true);
        }, 20);
      }
    }else{
      setNotices([])
    }
    scrollLogToBottom()

  }

  //停用
  const renderDebugButton_stop = useMemo(() => {
    if (!debugStatus) return <></>;
    const disableButton = (
      ![DebugStatus.showButton,DebugStatus.restart].includes(debugStatus)
      || running
    );
    const title =  debugStatus === DebugStatus.analysingFailed ? "代码中含有第三方依赖或其他原因, 不支持调试" : "(实验功能)";
    return (
      <Tooltip title={title}>
        <div
          className={`headerTab ccmButtons ${disableButton?"disabled":""}`}
          onClick={() => {
            // 调试按钮埋点
            try {
              postRobot.send(sourceWindow, 'debugCodeClick');
            } catch (error) {}

            if (disableButton) return;
            console.log("调试")
            startDebug()
          }}
        >
          {t(debugStatus)}
          <Icons.NewCorner color="#d81e06" size="24px"/>

        </div>
      </Tooltip>
    )
  },[debugStatus, running])

  function getLangLabel(item) {
    if (debugAbleLangs.includes(item.lang)) {
      return item.name + "(可调试)";
    }else {
      return item.name;
    }
  }
  const isDebugging = useMemo(()=>{
    return [DebugStatus.debugging, DebugStatus.restart].includes(debugStatus)
  },[debugStatus])

  return (
    <div style={{height: "100%"}}
         id="acm"
         onClick={(e) => {
           // console.log("Acm clicked")
          // setEnvListShow(false)
        }}
    >
      <FlexLayout ref={layoutRef}>
        <div id='acmCode' className={`editor content`}>
          <Spin spinning={pageLoading} fullscreen />
          {/* {customEnvModalOpen && <div className="envModalMask"></div>} */}
          <div id='codeHeaderId' className={`header  ${yuque|| "header-sep"}`}>
            <div id='headerTabKey' className={`headerTab ${langIndex === 0 ? "selected" : ""}`}
              onClick={() => {
                selectTab(0);
              }}
            >
              {/* {headertabEllapsed && <div className="headerTabEllapsed">...</div>} */}
              {!embedded && langIndex === 0 && (
                <select
                  value={lang[0]}
                  onChange={(e) => {
                    if (e.target.value === 'html') {
                      updateLang(['html', 'css', 'javascript']);
                    } else {
                      updateLang([e.target.value])
                    }
                    updateLangIndex(0)
                  }}
                  onClick={() => {
                    selectTab(0)
                  }}
                >
                  {Object.entries(langs).map(([key, item]) => (
                    <option key={key} value={key}>{getLangLabel(item)}</option>
                  ))}
                </select>
              )}
              {/* {yuque && t("代码")} */}
              {(embedded || langIndex > 0) && (
                <div>【{lang[0]}】</div>
              )}
              {/* 当前环境状态 */}
              {langConfig
                && (yuque || (!embedded && !inIframe) || fromVgen==='true') //只有语雀和独立场景、vgen可以使用自定义环境
                && (
                  <>
                <Space className="marginLeft12">
                  <Dropdown
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    overlayClassName="envlistDropdownDom"
                    overlayStyle={{ zIndex: '7' }}
                    open={envListShow || customEnvModalOpen}
                    trigger='none'
                    menu={envListMenu}
                    disabled={readOnly}
                    // env列表为空时的渲染
                    dropdownRender={envList.length > 1 || langConfig.lang === 'systemverilog' ? null : (menu) => (
                      <div className="envMenuRender">
                        {React.cloneElement(
                          menu,
                          { style: { boxShadow: 'none' } },
                        )}
                        <div className="emptyImg">
                          <img src={listEmptyPng} />
                        </div>
                        <div className="emptyTip">暂无预设实验室环境，可点击<span style={{ color: '#666666' }}>【自定义】</span>按钮，灵活搭配专属的实验室环境~</div>
                      </div>
                    )}
                  >
                    <Space className="blueText cursorPointer" style={{ color: readOnly ? '#262626' : '' }} onClick={(e) => {
                      if (readOnly) return;
                      setEnvListShow(!envListShow)
                      e.stopPropagation();
                    }}>
                      <span
                        title={selectedEnv?.name || (langConfig?.defaultEnvName || lang[0])}
                        className="envName"
                        style={{ maxWidth: envNameMaxWidth + 'px' }}
                      >
                        {selectedEnv?.name || (langConfig?.defaultEnvName || lang[0])}
                      </span>
                      {!readOnly && <div className={`envArrow ${envListShow ? 'flipped' : ''}`}>
                        <Icons.ArrowDown size='14px' />
                      </div>}
                    </Space>
                  </Dropdown>
                  {selectedEnv?.name !== envList[0]?.name && langConfig.lang !== 'systemverilog' && (
                    <>
                      <div className="envDivider"></div>
                      <div className="envStatus">
                        {/* 环境状态： */}
                        {envStatus === 0 && <span style={{ color: '#999999' }}>未启动</span>}
                        {envStatus === 1 && <span style={{ color: '#FAAD14' }}>启动中...</span>}
                        {envStatus === 2 && <span style={{ color: '#30C213' }}>已启动&nbsp;<Timer /></span>}
                      </div>
                    </>
                  )}
                </Space>
                <Modal
                  title = {""}
                  open={customEnvModalOpen}
                  footer={null}
                  zIndex={9}
                  width={660}
                  style={{ top: 'calc(50% - 226px)' }}
                  maskClosable={false}
                  onCancel={() => {
                    setCustomEnvModalOpen(false);
                    // const ss = setTimeout(() => {
                    //   setEnvListShow(true);
                    //   clearTimeout(ss);
                    // }, 0);
                  }}
                >
                  <CustomEnvModal
                      onClick = {(e) => {
                        e.stopPropagation();
                      }}
                      packageList={packageList}
                      setVisible={setCustomEnvModalOpen}
                      lang={lang[0]}
                      getEnvList={getEnvList}
                      setPageLoading={setPageLoading}
                      initPackages={initPackages}
                      langConfig = {langConfig}
                      authInfo = {authInfo}
                      setNewEnvId = {setNewEnvId}
                    />
                </Modal>
                </>
              )}
            </div>
            {lang[0] === "html" && (
              <>
                <div className={`headerTab ${langIndex === 1 ? "selected" : ""}`}
                     onClick={() => selectTab(1)}>CSS
                </div>
                <div className={`headerTab  ${langIndex === 2 ? "selected" : ""}`}
                     onClick={() => selectTab(2)}>Script
                </div>
              </>
            )}
          </div>

          <Editor
            key={langIndex}
            lang={lang[langIndex]}
            readOnly={readOnly}
            dark={dark}
            debugLine={isDebugging?debugLine:null}
            exception={exception}
            breakPoints={breakPoints}
            setBreakPoints={setBreakPoints}
            onChange={(e) => {
              let newCode = [...code]
              newCode[langIndex] = e;
              updateCode(newCode);
              //本地缓存
              if (newCode && newCode.length > 0 && newCode[0]) {
                localStorage.setItem(langKey(), JSON.stringify(newCode));
              }
            }}
            value={code[langIndex]}
          />

          {/* 底部按钮 */}
          <div className="bottomButtons">
            {/*自定义按钮*/}
            {customButtons && customButtons.map((button, index) => (
              <div
                key={index}
                className={`headerTab ccmButtons tooltip`}
                style={button.style}
                onClick={() => {
                  postRobot.send(sourceWindow, button.value, {code});
                }}
              >
                {t(button.name)}
              </div>
            ))}
            {/*运行*/}
            <div
              className={`headerTab ccmButtons run ${running ? "running" : ""}`}
              onClick={() => {
                runCode()
              }}
              //{...lang === "html" ? { 'data-tooltip': 'no need to run for HTML' } : {}}
            >
              {t("Run")}
            </div>
            {/*调试, 停用*/}
            {/*{renderDebugButton}*/}
            {/*打开新页面*/}
            {yuque && <div
              className={`headerTab ccmButtons tooltip`}
              onClick={() => {
                const targetWin = window.open("/", "blank")
                targetWin.addEventListener('load', () => {
                  postRobot.send(targetWin, 'send',{
                    dark,
                    aiEnabled:true,
                    code,lang
                  });
                });
              }}
            >
              {t("试一试")}
            </div>}
            {/*分享*/}
            {!embedded && (
              <div
                className={`headerTab ccmButtons tooltip`}
                onClick={() => {
                  if (noInput()) return;
                  if (!modified) {
                    setShowShare(true);
                    return;
                  }
                  //每次 share 生成新记录
                  saveCode(lang[0], code, null).then(codePiece => {
                    setShowShare(true)
                    navigate(`/${codePiece.oid}`); // 使用相对路径进行重定向
                  })
                }}
              >
                {t("Share")}
              </div>
            )}
            {/*新增*/}
            {oid && !embedded && (
              <div
                className={`headerTab ccmButtons tooltip ${running ? "running" : ""}`}
                onClick={() => {
                  updateCode([""])
                  updatePreview("")
                  navigate(`/`);
                }}
              >
                {t("Add")}
              </div>
            )}
            {/*更新回语雀*/}
            {yuque && modified && (
              <div
                className={`headerTab ccmButtons tooltip`}
                onClick={() => {
                  updateYuque();
                  toSetCloseCallback(null);
                }}
              >
                {t("更新到文档")}
              </div>
            )}
            {/*取消修改*/}
            {modified && (
              <div
                className={`headerTab ccmButtons icon`}
                title="取消修改"
                onClick={cancelEdit}
                style={{ border: 'none', margin: 0 }}
              >
                <Icons.Undo/>
              </div>
            )}
            {/*开关灯*/}
            {embedded || (dark ?
              (<div className={`headerTab icon ccmButtons`}
                                title="开灯"
                                onClick={() => setDark(false)}
            ><Icons.Light/></div>):
              (<div className={`headerTab icon ccmButtons`}
                    title="关灯"
                    onClick={() => setDark(true)}
              ><Icons.Dark/></div>)
            )}
          </div>
        </div>


        <FlexLayout ratio={isDebugging ? "20%" : "100%"}>
          {/*输出结果*/}

          <div className={`output content`}>
            <div className={`header`}>
              <div className={`headerTab selected`}>
                {isDebugging?t("调试输出"):t("运行结果")}
              </div>
            </div>
            <>
              {lang[0] !== "html" && (
                <div className={`result ${embedded ? "embedded" : ""}`}
                     tabIndex={0}
                     onKeyDown={handleKeyPress}
                     onPaste={handlePaste}
                >
                  {yuque ? (
                    <div className={`${loading ? "loading" : "hide"}`}><Icons.YuqueLoading/>{t('正在执行...')}</div>
                  ) : (
                    <div className={`${loading ? "loading" : "hide"}`}>Running</div>
                  )}
                  {svgResult && (
                    <div
                      className="svg-result"
                      dangerouslySetInnerHTML={{ __html: svgResult }}
                    />
                  )}
                  <code ref={terminalRef} dangerouslySetInnerHTML={{__html: he.encode(preview)}}
                        style={{whiteSpace: 'pre-wrap'}}></code>
                  <div>
                    {notices.map((notice, i) =>
                      <p key={i} className={`notice ${notice.success ? "success" : ""}`}>{notice.msg}</p>
                    )}
                  </div>
                  <div className="resultButtons">
                    {(debugStatus === DebugStatus.showButton
                        || debugStatus === DebugStatus.analysing
                        || debugStatus === DebugStatus.analysingFailed
                      ) && runFinished
                      //停用 && renderDebugButton
                    }
                  </div>
                  {showVgenAdv && !vgenAdvDismissed && <>
                    <VgenAdv setShowVgenAdv={setShowVgenAdv} setVgenAdvDismissed={setVgenAdvDismissed}/>
                  </>}
                </div>
              )}
              {lang[0] === "html" && (
                <div className={`html ${embedded ? "embedded" : ""}`}>
                  <iframe
                    title="HTML Content"
                    style={{width: '100%', height: '100%', border: 'none'}}
                    sandbox="allow-scripts"
                  srcDoc={htmlCode}
                  />
                </div>
              )}
            </>
          </div>
          {/*调试*/}
          <div className={`output content`}>
            <div className={`header`}>
              <div className={`headerTab selected`}>
                {t("Debug")}
              </div>
            </div>
            {isDebugging&&<Debug dat={debugDat} onStep={onStep} breakPoints={breakPoints} lang={lang[0]}
                                 onError={(e)=>{
                                   console.log("DebugError",e)
                                   setDebugStatus(DebugStatus.analysingFailed)
                                 }}
            />}
          </div>
        </FlexLayout>
      </FlexLayout>
      {showShare && (
        <div className="modal-overlay" onClick={() => {
          setShowShare(false)
        }}>
          <div className="modal" onClick={(event) => {
              event.stopPropagation()
            }}>
              <ShareResult
                  oid={oid}
              />
            </div>
          </div>
      )}

      {/* vgen的AI助手 */}
      {fromVgen === 'true' && runFailed && lang !== "html" &&  (
        <div
          id='vgenAiAssistant'
          style={{ right: '19px' }}
          className={`run share ai-assistant ${runFailed ? "" : "disabled"}`}
          onClick={() => {
            postRobot.send(sourceWindow, 'errorHandle', {lang:lang[0], code:code[0], error:preview});
          }}
          title={runFailed ? "点击查看诊断结果" : "仅在运行代码出错时可用"}
      >
        错误诊断
      </div>
      )}

    </div>


  );
});

export default Acm;
